import { useTranslation } from "react-i18next";
import { STIMULUS_OFF, STIMULUS_ON } from "../constants";
import { ROW, ROW_LABEL } from "../styles";
import type { HeadsetSettings } from "../types";
import { Switch } from "./Switch";

export type SettingsRowsProps = {
  /** the whole bag, straight from useHeadsetSettings */
  settings: HeadsetSettings;
};

/**
 * The headset settings block: the two settings the backend actually implements.
 *
 * Takes the settings bag rather than a value/gate/handler triple per row, so a new
 * row is a key on HeadsetSettings and a block below — nothing between the hook and
 * here has to learn about it.
 *
 * The design has three more rows — Language, Audio instructions and Short workflow.
 * None of them has a field to read or a command to write, so they are not rendered
 * at all rather than shown dead; ../types.ts lists what each one needs.
 */
export function SettingsRows({ settings }: SettingsRowsProps) {
  const { t } = useTranslation();

  // Nothing on screen names the two positions — the design is a bare switch — so
  // the switch says it to a screen reader and on hover instead.
  const stimulusLabel = t("HeadsetDock.StimulusSwitch", {
    on: STIMULUS_ON,
    off: STIMULUS_OFF,
  });

  return (
    <>
      {/* Stimulus. swap_stimulus is relative, which is exactly what a switch needs
          as long as there are two stimuli; see STIMULUS_ON in ../constants. Live on
          Bird, dead on Robot: the swap only goes one way from here. */}
      <div className={ROW}>
        <span className={ROW_LABEL}>{t("HeadsetDock.Stimulus")}</span>
        <Switch
          checked={settings.stimulus.on}
          label={stimulusLabel}
          title={stimulusLabel}
          disabled={!settings.stimulus.enabled}
          onChange={settings.stimulus.toggle}
        />
      </div>

      {/* Subtitles. Deliberately does NOT lock during a session: captions can come
          and go mid-run. */}
      <div className={ROW}>
        <span className={ROW_LABEL}>{t("HeadsetDock.Subtitles")}</span>
        <Switch
          checked={settings.subtitles.on}
          label={t("HeadsetDock.Subtitles")}
          disabled={!settings.subtitles.enabled}
          onChange={settings.subtitles.toggle}
        />
      </div>
    </>
  );
}
